import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useSubTour } from './tour/SubTourContext';
import { useTour } from './tour/TourContext';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Progress } from './ui/progress';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from './ui/dialog';
import { 
  CheckCircle2, 
  Circle, 
  Rocket, 
  ChevronRight,
  X,
  PartyPopper,
  Sparkles
} from 'lucide-react';

const QuickStartChecklist = () => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const { startSubTour } = useSubTour();
  const { startTour } = useTour();
  
  const [checklistData, setChecklistData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tourPromptOpen, setTourPromptOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [dismissing, setDismissing] = useState(false);

  useEffect(() => {
    fetchChecklistStatus();
  }, []);

  const fetchChecklistStatus = async () => {
    try {
      const response = await api.get('/onboarding/checklist-status');
      setChecklistData(response.data);
    } catch (error) {
      console.error('Failed to fetch checklist status:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleItemClick = (item) => {
    if (item.completed) return;
    
    // Special handling for the onboarding tour
    if (item.id === 'tour') {
      // Navigate to dashboard and start the tour
      navigate('/dashboard');
      setTimeout(() => {
        startTour();
      }, 100);
      return;
    }
    
    if (!item.link) return;
    
    if (item.sub_tour) {
      // Show prompt asking if user wants to start sub-tour
      setSelectedItem(item);
      setTourPromptOpen(true);
    } else {
      // Navigate directly
      navigate(item.link);
    }
  };

  const handleTourChoice = (startTour) => {
    setTourPromptOpen(false);
    if (startTour && selectedItem?.sub_tour) {
      startSubTour(selectedItem.sub_tour);
    } else if (selectedItem?.link) {
      navigate(selectedItem.link);
    }
    setSelectedItem(null);
  };

  const handleDismiss = async () => {
    setDismissing(true);
    try {
      await api.patch('/onboarding/checklist-dismiss');
      setChecklistData(prev => ({ ...prev, checklist_dismissed: true }));
    } catch (error) {
      console.error('Failed to dismiss checklist:', error);
    } finally {
      setDismissing(false);
    }
  };

  // Check if celebration should be shown (within 1 week of completion)
  const shouldShowCelebration = () => {
    if (!checklistData?.all_required_complete || !checklistData?.checklist_completed_at) {
      return false;
    }
    const completedAt = new Date(checklistData.checklist_completed_at);
    const oneWeekAgo = new Date();
    oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
    return completedAt > oneWeekAgo;
  };

  if (loading) {
    return (
      <Card className="card-industrial">
        <CardContent className="p-6">
          <div className="h-32 bg-muted animate-pulse rounded" />
        </CardContent>
      </Card>
    );
  }

  if (!checklistData || checklistData.checklist_dismissed) {
    return null;
  }

  // Show celebration card if all required items complete and within 1 week
  if (checklistData.all_required_complete && shouldShowCelebration()) {
    return (
      <Card className="card-industrial border-[#ffd108] bg-gradient-to-br from-[#ffd108]/10 via-[#ffd108]/5 to-transparent overflow-hidden relative">
        <div className="absolute top-0 right-0 w-32 h-32 bg-[#ffd108]/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-24 h-24 bg-[#ffd108]/10 rounded-full blur-2xl" />
        <CardContent className="p-6 relative">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-[#ffd108]/20 rounded-xl">
              <PartyPopper className="h-8 w-8 text-[#ffd108]" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <h3 className="font-heading font-bold text-xl">Setup Complete!</h3>
                <Sparkles className="h-5 w-5 text-[#ffd108]" />
              </div>
              <p className="text-muted-foreground mb-4">
                Congratulations! You've completed all the essential setup tasks. 
                Your EQUIPR workspace is ready to go.
              </p>
              <div className="flex items-center gap-3">
                <Button 
                  onClick={() => navigate('/schedule')}
                  className="btn-volt"
                >
                  <Rocket className="h-4 w-4 mr-2" />
                  Start Scheduling
                </Button>
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={handleDismiss}
                  disabled={dismissing}
                  className="text-muted-foreground hover:text-foreground"
                >
                  {dismissing ? 'Dismissing...' : 'Dismiss'}
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Hide if all required complete but celebration period has passed
  if (checklistData.all_required_complete && !shouldShowCelebration()) {
    return null;
  }

  const progressPercent = Math.round((checklistData.completed_required / checklistData.total_required) * 100);

  return (
    <>
      <Card className="card-industrial border-[#ffd108]/30" data-testid="quick-start-checklist">
        <CardContent className="p-6">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-[#ffd108]/20 rounded-lg">
                <Rocket className="h-5 w-5 text-[#ffd108]" />
              </div>
              <div>
                <h3 className="font-heading font-semibold text-lg">Quick Start</h3>
                <p className="text-sm text-muted-foreground">
                  {checklistData.completed_required} of {checklistData.total_required} tasks completed
                </p>
              </div>
            </div>
            <Button 
              variant="ghost" 
              size="icon"
              onClick={handleDismiss}
              disabled={dismissing}
              className="h-8 w-8 text-muted-foreground hover:text-foreground"
              title="Dismiss checklist"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>

          {/* Progress Bar */}
          <div className="mb-4">
            <Progress value={progressPercent} className="h-2" />
            <p className="text-xs text-muted-foreground mt-1 text-right">{progressPercent}%</p>
          </div>

          {/* Checklist Items */}
          <div className="space-y-2">
            {checklistData.items.map((item) => (
              <button
                key={item.id}
                onClick={() => handleItemClick(item)}
                disabled={item.completed || !item.link}
                className={`w-full flex items-center gap-3 p-3 rounded-lg transition-colors text-left ${
                  item.completed 
                    ? 'bg-green-500/10 text-green-600 dark:text-green-400' 
                    : item.link 
                      ? 'bg-muted/50 hover:bg-muted cursor-pointer' 
                      : 'bg-muted/30 opacity-60'
                }`}
                data-testid={`checklist-item-${item.id}`}
              >
                {item.completed ? (
                  <CheckCircle2 className="h-5 w-5 flex-shrink-0" />
                ) : (
                  <Circle className="h-5 w-5 flex-shrink-0 text-muted-foreground" />
                )}
                <span className={`flex-1 text-sm ${item.completed ? 'line-through opacity-70' : ''}`}>
                  {item.label}
                </span>
                {!item.completed && item.link && (
                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                )}
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Tour Prompt Dialog */}
      <Dialog open={tourPromptOpen} onOpenChange={setTourPromptOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">Start Guided Tour?</DialogTitle>
            <DialogDescription>
              Would you like a step-by-step guide to help you {selectedItem?.label.toLowerCase()}?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => handleTourChoice(false)}>
              No, take me there
            </Button>
            <Button className="btn-volt" onClick={() => handleTourChoice(true)}>
              Yes, start guide
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default QuickStartChecklist;
